import _ from 'lodash';
import { VantageNames, isReasonCodesExtensionName, reasonCodesExtensionName } from '@/vantage/constants';
import reasonCodesExtension from '@/vantage/extensions/reason_codes.json';
import { SeeqNames } from '@seeqdev/constants';
import type { ExtensionSchema } from '@/vantage/types/Extension.types';
import type { ExtensionColumns } from '@/vantage/types/TableConfig.types';
import type { ReasonCodeConditionAssignmentOutputV1 } from '@seeqdev/api/model/ReasonCodeConditionAssignmentOutputV1';

export type AssignedReasonCodeHierarchy = { id: string; name: string; levels: string[] };

/** The reason code sets a button offers, by hierarchy id; null stands for the legacy reason codes table. */
export type ServedReasonCodeSets = Array<string | null>;

export const REASON_CODE_AUTHOR_COLUMN_NAME = 'Author';

export const REASON_CODE_COLUMN_PREFIX = `${SeeqNames.SeeqContextNameSpace.ReasonCodeNamespace}.`;

export const REASON_CODE_AUTHOR_VIRTUAL_COLUMN = `${REASON_CODE_COLUMN_PREFIX}author`;

/** The backend virtual column name a reason code column's values are fetched from. */
export const toReasonCodeVirtualColumnName = (columnName: string, category: string): string =>
  columnName === REASON_CODE_AUTHOR_COLUMN_NAME
    ? REASON_CODE_AUTHOR_VIRTUAL_COLUMN
    : `${REASON_CODE_COLUMN_PREFIX}${category}`;

/** Organizer has serialized extensions, not the workbook store map. */
export const reasonCodeColumnLookupFromExtensionColumns = (
  extensions?: ExtensionColumns,
): { reasonCodeColumnNames: string[]; reasonCodeColumnNameMap: Record<string, string> } => {
  const reasonCodeColumnNameMap: Record<string, string> = {};
  for (const [displayName, column] of Object.entries(extensions ?? {})) {
    const { category, colDefName, kindIdentifierPairs } = column.meta;
    if (!category) {
      continue;
    }
    const capsuleIdentifier = kindIdentifierPairs?.[VantageNames.Kinds.Capsule];
    if (
      capsuleIdentifier &&
      !isReasonCodesExtensionName(capsuleIdentifier) &&
      !capsuleIdentifier.startsWith(REASON_CODE_COLUMN_PREFIX)
    ) {
      continue;
    }
    const name = colDefName ?? displayName;
    reasonCodeColumnNameMap[displayName] = toReasonCodeVirtualColumnName(name, category);
  }
  return {
    reasonCodeColumnNames: [...new Set(Object.values(reasonCodeColumnNameMap))],
    reasonCodeColumnNameMap,
  };
};

export type ReasonCodeRouting = {
  explicit: Record<string, string | undefined>;
  defaultHierarchyId?: string;
};

/** Builds the routing for a room's assignments; the row without a conditionId is the room default. */
export const reasonCodeRoutingFromAssignments = (
  assignments: ReasonCodeConditionAssignmentOutputV1[],
): ReasonCodeRouting => ({
  explicit: Object.fromEntries(
    assignments
      .filter((a) => a.conditionId && a.hierarchyCategoryId)
      .map((a) => [a.conditionId!.toLowerCase(), a.hierarchyCategoryId!]),
  ),
  defaultHierarchyId: assignments.find((a) => a.conditionId == null && a.hierarchyCategoryId)?.hierarchyCategoryId,
});

/** The hierarchy covering a condition: its explicit assignment wins, else the room default. */
export const reasonCodeHierarchyFor = (
  routing: ReasonCodeRouting | undefined,
  conditionId: string | undefined,
): string | undefined =>
  routing
    ? ((conditionId ? routing.explicit[conditionId.toLowerCase()] : undefined) ?? routing.defaultHierarchyId)
    : undefined;

/** Whether a button serving these sets offers every condition's set; a button without declared sets serves all. */
export const servesConditions = (
  servedReasonCodeSets: ServedReasonCodeSets | undefined,
  routing: ReasonCodeRouting | undefined,
  conditionIds: string[],
): boolean =>
  !servedReasonCodeSets ||
  conditionIds.every((conditionId) =>
    servedReasonCodeSets.includes(reasonCodeHierarchyFor(routing, conditionId) ?? null),
  );

// The backend resolves reasonCode.<category> by category name, so hierarchies sharing a level name share a column.
const hierarchyColumns = (
  hierarchies: AssignedReasonCodeHierarchy[],
): NonNullable<NonNullable<ExtensionSchema['uiConfig']>['columns']> => {
  const levels = _.uniq(hierarchies.flatMap((hierarchy) => hierarchy.levels));
  if (!levels.length) {
    return [];
  }
  const levelColumns = levels.map((level) => ({
    colDefName: level,
    colDefType: 'string' as const,
    colDefFilterType: 'set' as const,
    category: level,
  }));
  return [
    ...levelColumns,
    {
      colDefName: REASON_CODE_AUTHOR_COLUMN_NAME,
      colDefType: 'string' as const,
      colDefFilterType: 'set' as const,
      category: levels[0],
    },
  ];
};

const buildReasonCodesSchema = (
  base: ExtensionSchema,
  hierarchies: AssignedReasonCodeHierarchy[],
): ExtensionSchema => ({
  ...base,
  name: VantageNames.Extensions.ReasonCodes,
  uiConfig: {
    ...base.uiConfig,
    columns: _.uniqBy([...(base.uiConfig?.columns ?? []), ...hierarchyColumns(hierarchies)], (column) =>
      column.colDefName === REASON_CODE_AUTHOR_COLUMN_NAME ? REASON_CODE_AUTHOR_COLUMN_NAME : column.category,
    ),
  },
});

const servingOnly = (extension: ExtensionSchema, servedReasonCodeSets: ServedReasonCodeSets): ExtensionSchema => ({
  ...extension,
  uiConfig: {
    ...extension.uiConfig,
    extensionSpecificConfiguration: { ...extension.uiConfig?.extensionSpecificConfiguration, servedReasonCodeSets },
  },
});

const buildHierarchyButtonSchema = (base: ExtensionSchema, hierarchy: AssignedReasonCodeHierarchy): ExtensionSchema => {
  const schema = buildReasonCodesSchema(base, [hierarchy]);
  return servingOnly(
    {
      ...schema,
      name: reasonCodesExtensionName(hierarchy.id),
      uiConfig: {
        ...schema.uiConfig,
        iconRepresentation: {
          ...schema.uiConfig?.iconRepresentation,
          // Hierarchy names are user data, not i18n keys, so set `label` and clear `labelTranslationKey`.
          discreteStates: schema.uiConfig?.iconRepresentation?.discreteStates?.map((state) => ({
            ...state,
            labelTranslationKey: undefined,
            label: hierarchy.name,
          })),
        },
      },
    },
    [hierarchy.id],
  );
};

export const mergeReasonCodeExtensions = (
  persistedExtensions: ExtensionSchema[],
  legacyReasonCodeTableId: string | undefined,
  hierarchies: AssignedReasonCodeHierarchy[] | undefined,
  isButtonPerHierarchy: boolean,
): ExtensionSchema[] => {
  // Feature inactive (flag off or not loaded): passthrough.
  if (hierarchies === undefined) {
    return persistedExtensions;
  }
  const [persistedReasonCodes, otherExtensions] = _.partition(persistedExtensions, (extension) =>
    isReasonCodesExtensionName(extension.name),
  );
  // A legacy room (reason codes materialized table present) keeps its persisted button.
  const legacyExtension = legacyReasonCodeTableId
    ? persistedReasonCodes.find((extension) => extension.name === VantageNames.Extensions.ReasonCodes)
    : undefined;
  if (!hierarchies.length) {
    return legacyExtension ? [...otherExtensions, legacyExtension] : otherExtensions;
  }
  if (isButtonPerHierarchy) {
    return [
      ...otherExtensions,
      ...(legacyExtension ? [servingOnly(legacyExtension, [null])] : []),
      ...hierarchies.map((hierarchy) => buildHierarchyButtonSchema(reasonCodesExtension as ExtensionSchema, hierarchy)),
    ];
  }
  return [
    ...otherExtensions,
    buildReasonCodesSchema(legacyExtension ?? (reasonCodesExtension as ExtensionSchema), hierarchies),
  ];
};
